import {useEffect, useMemo, useRef, useState} from "react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {
    ArrowLeft,
    BadgeDollarSign,
    CircleCheckBig,
    Landmark,
    PauseCircle,
    PlayCircle,
    RefreshCw,
    RotateCcw,
    SlidersHorizontal,
    WalletCards,
    XCircle,
} from "lucide-react";
import {Link, useParams} from "react-router-dom";
import {
    COUNTRY_SCOPE_STORAGE_KEY,
    getAdminCountryScope,
    resolveAdminCountryScope,
} from "../../shared/api/countryScope";
import {useCapabilities} from "../../shared/auth/CapabilitiesContext";
import {AdminTable} from "../../shared/components/AdminTable";
import {DataState} from "../../shared/components/DataState";
import {DetailGrid} from "../../shared/components/DetailGrid";
import {MutationResultBanner} from "../../shared/components/MutationResultBanner";
import {StatusBadge} from "../../shared/components/StatusBadge";
import {formatExactMoney} from "../../shared/finance/decimal";
import {mapFinanceError} from "../../shared/finance/errors";
import {formatDateTime, formatNumber} from "../../shared/utils/format";
import {useTranslation} from "../../shared/i18n";
import {
    approveSettlement,
    cancelSettlement,
    createSettlementAdjustment,
    getSettlement,
    holdSettlement,
    listSettlementPayouts,
    markSettlementPaid,
    markSettlementPartiallyPaid,
    releaseSettlementHold,
    reopenSettlement,
    sendSettlementToBank,
    settlementKeys,
} from "./api";
import {
    getReadinessPresentation,
    getReadinessReasonLabel,
    getSettlementActionPresentation,
    getSettlementActions,
    getSettlementStatusPresentation,
} from "./statusMachine";
import {
    buildSettlementCommandPayload,
    SettlementCommandDialog,
} from "./components/SettlementCommandDialog";
import "./settlements.css";

const ACTION_ICONS = {
    approve: CircleCheckBig,
    reopen: RotateCcw,
    adjust: SlidersHorizontal,
    hold: PauseCircle,
    release_hold: PlayCircle,
    cancel: XCircle,
    send_to_bank: Landmark,
    partial_paid: WalletCards,
    mark_paid: BadgeDollarSign,
};

const ACTION_REQUESTS = {
    approve: approveSettlement,
    reopen: reopenSettlement,
    cancel: cancelSettlement,
    hold: holdSettlement,
    release_hold: releaseSettlementHold,
    adjust: createSettlementAdjustment,
    send_to_bank: sendSettlementToBank,
    partial_paid: markSettlementPartiallyPaid,
    mark_paid: markSettlementPaid,
};

const validId = (value) => /^[1-9]\d*$/.test(value || "");

const snapshotExtraEntries = (snapshot) =>
    Object.entries(snapshot || {}).filter(
        ([key]) => !["company", "currency", "branch", "period", "amounts"].includes(key),
    );

const ActionRail = ({actions, disabled, focusRef, onOpen}) => {
    const {t} = useTranslation();
    return (
    <aside
        ref={focusRef}
        className="settlement-action-rail"
        aria-label={t("money.settlements.detail.commandsAria")}
        tabIndex={-1}
    >
        <p className="admin-eyebrow">{t("money.settlements.detail.actionsEyebrow")}</p>
        <h2>{t("money.settlements.detail.actionsTitle")}</h2>
        {actions.length ? (
            <div className="settlement-action-list">
                {actions.map((action) => {
                    const presentation = getSettlementActionPresentation(action);
                    const Icon = ACTION_ICONS[action];
                    return (
                        <button
                            key={action}
                            className="admin-button admin-button-secondary"
                            type="button"
                            disabled={disabled}
                            onClick={() => onOpen(action)}
                        >
                            {Icon ? <Icon aria-hidden="true" size={16} /> : null}
                            {t(`money.settlements.actions.${action}`, {defaultValue: presentation.label})}
                        </button>
                    );
                })}
            </div>
        ) : (
            <p className="admin-muted">{t("money.settlements.detail.noActions")}</p>
        )}
        {disabled && actions.length ? (
            <p className="settlement-scope-warning">{t("money.settlements.scopeWarning")}</p>
        ) : null}
    </aside>
    );
};

export const SettlementDetailPage = () => {
    const {t} = useTranslation();
    const {settlementId} = useParams();
    const numericSettlementId = validId(settlementId) ? Number(settlementId) : null;
    const queryClient = useQueryClient();
    const capabilities = useCapabilities();
    const [countryScope, setCountryScope] = useState(() =>
        resolveAdminCountryScope({
            role: capabilities.user?.role,
            allowedCountryIds: capabilities.scope?.country_ids || [],
            storedScope:
                localStorage.getItem(COUNTRY_SCOPE_STORAGE_KEY) ?? getAdminCountryScope(),
        }),
    );
    const activeCountryScopeRef = useRef(countryScope);
    const [command, setCommand] = useState(null);
    const [commandError, setCommandError] = useState(null);
    const [mutationResult, setMutationResult] = useState(null);
    const actionRailRef = useRef(null);
    const detailKey = settlementKeys.detail(countryScope, numericSettlementId);
    const payoutsKey = settlementKeys.payouts(countryScope, numericSettlementId);

    const detailQuery = useQuery({
        queryKey: detailKey,
        queryFn: () =>
            getSettlement({settlementId: numericSettlementId, countryScope}),
        enabled: numericSettlementId !== null && countryScope !== null,
        retry: false,
    });
    const payoutsQuery = useQuery({
        queryKey: payoutsKey,
        queryFn: () => listSettlementPayouts({settlementId: numericSettlementId}),
        enabled: numericSettlementId !== null && countryScope !== null,
        retry: false,
    });

    useEffect(() => {
        const handleCountryChange = (event) => {
            const nextScope = event.detail?.countryId;
            const normalizedScope = nextScope == null ? null : String(nextScope);
            activeCountryScopeRef.current = normalizedScope;
            setCountryScope(normalizedScope);
            setCommand(null);
            setCommandError(null);
            setMutationResult(null);
        };
        window.addEventListener("sorpa-admin-country-scope-change", handleCountryChange);
        return () =>
            window.removeEventListener("sorpa-admin-country-scope-change", handleCountryChange);
    }, []);

    const commandMutation = useMutation({
        mutationFn: async ({action, values, requestSettlementId}) => {
            const payload = buildSettlementCommandPayload(action, values);
            const request = ACTION_REQUESTS[action];
            return request(
                payload === undefined
                    ? {settlementId: requestSettlementId}
                    : {settlementId: requestSettlementId, payload},
            );
        },
        onSuccess: (result, variables) => {
            const requestDetailKey = settlementKeys.detail(
                variables.requestScope,
                variables.requestSettlementId,
            );
            const requestPayoutsKey = settlementKeys.payouts(
                variables.requestScope,
                variables.requestSettlementId,
            );
            queryClient.setQueryData(requestDetailKey, result.settlement);
            void queryClient.invalidateQueries({
                queryKey: ["settlements", String(variables.requestScope), "list"],
            });
            void queryClient.invalidateQueries({queryKey: requestPayoutsKey});
            if (activeCountryScopeRef.current !== variables.requestScope) return;
            setCommand(null);
            setCommandError(null);
            setMutationResult({
                kind: "success",
                message: t("money.settlements.detail.updated", {id: result.settlement.id}),
            });
        },
        onError: async (error, variables) => {
            const mapped = mapFinanceError(error);
            if (mapped.kind === "conflict") {
                const requestDetailKey = settlementKeys.detail(
                    variables.requestScope,
                    variables.requestSettlementId,
                );
                const requestPayoutsKey = settlementKeys.payouts(
                    variables.requestScope,
                    variables.requestSettlementId,
                );
                if (activeCountryScopeRef.current === variables.requestScope) {
                    setCommand(null);
                    setCommandError(null);
                    setMutationResult(mapped);
                }
                await Promise.all([
                    queryClient.invalidateQueries({queryKey: requestDetailKey}),
                    queryClient.invalidateQueries({
                        queryKey: ["settlements", String(variables.requestScope), "list"],
                    }),
                    queryClient.invalidateQueries({queryKey: requestPayoutsKey}),
                ]);
                if (activeCountryScopeRef.current === variables.requestScope) {
                    actionRailRef.current?.focus();
                }
                return;
            }
            if (activeCountryScopeRef.current !== variables.requestScope) return;
            setCommandError(mapped);
        },
    });

    const statement = detailQuery.data;
    const payouts = payoutsQuery.data || [];
    const status = statement
        ? getSettlementStatusPresentation(statement.status)
        : null;
    const readiness = statement
        ? getReadinessPresentation(statement.readiness_status)
        : null;
    const currency = statement?.snapshot?.currency?.code || "";
    const actions = statement
        ? getSettlementActions(statement, capabilities.hasAction)
        : [];
    const snapshotCompany = statement?.snapshot?.company || {};
    const snapshotExtras = useMemo(
        () => snapshotExtraEntries(statement?.snapshot),
        [statement?.snapshot],
    );

    const submitCommand = (values) => {
        setCommandError(null);
        try {
            buildSettlementCommandPayload(command, values);
        } catch (error) {
            setCommandError({message: error.message, fieldErrors: {}});
            return;
        }
        commandMutation.mutate({
            action: command,
            values,
            requestScope: countryScope,
            requestSettlementId: numericSettlementId,
        });
    };

    if (numericSettlementId === null) {
        return <DataState title={t("money.settlements.detail.invalidTitle")} copy={t("money.settlements.detail.invalidCopy")} />;
    }

    if (detailQuery.isLoading) {
        return <div className="admin-table-skeleton" aria-label={t("money.settlements.detail.loading")}><span /><span /><span /></div>;
    }

    if (detailQuery.isError || !statement) {
        return (
            <DataState
                title={t("money.settlements.detail.loadError")}
                copy={mapFinanceError(detailQuery.error).message}
            />
        );
    }

    const lineColumns = [
        {key: "source", label: t("money.settlements.detail.source"), render: (item) => `${item.source_table} / ${item.source_id}`},
        {key: "branch", label: t("money.settlements.detail.branch"), render: (item) => item.branch_name || `#${item.branch_id}`},
        {key: "occurred", label: t("money.settlements.detail.occurred"), render: (item) => formatDateTime(item.occurred_at)},
        {key: "gross", label: t("money.settlements.detail.gross"), render: (item) => formatExactMoney(item.gross_amount)},
        {key: "commission", label: t("money.settlements.detail.commission"), render: (item) => formatExactMoney(item.commission_amount)},
        {key: "payable", label: t("money.settlements.detail.payable"), render: (item) => formatExactMoney(item.payable_amount)},
    ];

    const payoutColumns = [
        {key: "id", label: t("money.settlements.detail.tranche"), render: (item) => `#${item.id}`},
        {key: "amount", label: t("money.settlements.detail.amount"), render: (item) => formatExactMoney(item.amount, currency)},
        {key: "reference", label: t("money.settlements.detail.reference"), render: (item) => item.payment_reference || "—"},
        {key: "paid_at", label: t("money.settlements.detail.paidAt"), render: (item) => formatDateTime(item.paid_at)},
        {key: "actor", label: t("money.settlements.detail.operator"), render: (item) => item.paid_by_id ? `#${item.paid_by_id}` : "—"},
    ];

    return (
        <div className="admin-page settlement-detail-page">
            <Link className="settlement-back-link" to="/settlements">
                <ArrowLeft aria-hidden="true" size={16} /> {t("money.settlements.detail.back")}
            </Link>
            <header className="admin-page-header settlement-detail-header">
                <div>
                    <p className="admin-eyebrow">{t("money.settlements.detail.eyebrow")}</p>
                    <h1 className="admin-page-title">{t("money.settlements.detail.title", {id: statement.id})}</h1>
                    <p className="admin-page-copy">
                        {statement.company_name} · {formatDateTime(statement.period_start)} — {formatDateTime(statement.period_end)}
                    </p>
                </div>
                <div className="settlement-status-pair">
                    <StatusBadge tone={status.tone}>{t(`money.settlements.statuses.${status.known ? statement.status : "unknown"}`)}</StatusBadge>
                    <StatusBadge tone={readiness.tone}>{t(`money.settlements.readinessStatuses.${readiness.known ? statement.readiness_status : "unknown"}`)}</StatusBadge>
                </div>
            </header>

            <MutationResultBanner result={mutationResult} />

            <div className="settlement-detail-layout">
                <main className="settlement-detail-main">
                    <section className="settlement-money-band" aria-label={t("money.settlements.detail.amountsAria")}>
                        <span><small>{t("money.settlements.detail.gross")}</small><strong>{formatExactMoney(statement.gross_amount, currency)}</strong></span>
                        <span><small>{t("money.settlements.detail.commission")}</small><strong>{formatExactMoney(statement.commission_amount, currency)}</strong></span>
                        <span><small>{t("money.settlements.detail.adjustment")}</small><strong>{formatExactMoney(statement.adjustment_amount, currency)}</strong></span>
                        <span><small>{t("money.settlements.detail.payable")}</small><strong>{formatExactMoney(statement.payable_amount, currency)}</strong></span>
                    </section>

                    <section className="settlement-section" aria-labelledby="settlement-context-title">
                        <h2 id="settlement-context-title">{t("money.settlements.detail.contextTitle")}</h2>
                        <DetailGrid items={[
                            {label: t("money.settlements.detail.company"), value: statement.company_name},
                            {label: t("money.settlements.detail.branch"), value: statement.branch_name || t("money.settlements.detail.allBranches")},
                            {label: t("money.settlements.detail.level"), value: statement.settlement_scope},
                            {label: t("money.settlements.detail.purchases"), value: formatNumber(statement.purchase_count)},
                            {label: t("money.settlements.detail.refunds"), value: formatNumber(statement.refund_count)},
                            {label: t("money.settlements.detail.created"), value: formatDateTime(statement.created_at)},
                        ]} />
                    </section>

                    <section className="settlement-section" aria-labelledby="settlement-snapshot-title">
                        <h2 id="settlement-snapshot-title">{t("money.settlements.detail.snapshotTitle")}</h2>
                        <DetailGrid items={[
                            {label: t("money.settlements.detail.legalName"), value: snapshotCompany.legal_name || "—"},
                            {label: t("money.settlements.detail.bin"), value: snapshotCompany.bin_number || "—"},
                            {label: t("money.settlements.detail.account"), value: snapshotCompany.account_number || "—"},
                            {label: t("money.settlements.detail.currency"), value: currency || "—"},
                            ...snapshotExtras.map(([key, value]) => ({
                                label: key,
                                value: typeof value === "string" ? value : JSON.stringify(value),
                            })),
                        ]} />
                    </section>

                    <section className="settlement-section" aria-labelledby="settlement-readiness-title">
                        <h2 id="settlement-readiness-title">{t("money.settlements.readiness")}</h2>
                        {statement.readiness_reasons.length ? (
                            <ul className="settlement-reason-list">
                                {statement.readiness_reasons.map((reason) => (
                                    <li key={reason}>{t(`money.settlements.readinessReasons.${reason}`, {defaultValue: getReadinessReasonLabel(reason)})}</li>
                                ))}
                            </ul>
                        ) : (
                            <p className="admin-muted">{t("money.settlements.detail.noReasons")}</p>
                        )}
                    </section>

                    <section className="settlement-section" aria-labelledby="settlement-lines-title">
                        <h2 id="settlement-lines-title">{t("money.settlements.detail.linesTitle")}</h2>
                        <AdminTable columns={lineColumns} rows={statement.lines} emptyText={t("money.settlements.detail.linesEmpty")} />
                    </section>

                    <section className="settlement-section" aria-labelledby="settlement-payouts-title">
                        <h2 id="settlement-payouts-title">{t("money.settlements.detail.payoutsTitle")}</h2>
                        {payoutsQuery.isError ? (
                            <DataState title={t("money.settlements.detail.payoutsError")} copy={mapFinanceError(payoutsQuery.error).message} />
                        ) : (
                            <AdminTable columns={payoutColumns} rows={payouts} loading={payoutsQuery.isLoading} emptyText={t("money.settlements.detail.payoutsEmpty")} />
                        )}
                    </section>

                    <section className="settlement-section" aria-labelledby="settlement-lifecycle-title">
                        <h2 id="settlement-lifecycle-title">{t("money.settlements.detail.lifecycleTitle")}</h2>
                        <DetailGrid items={[
                            {label: t("money.settlements.detail.approved"), value: formatDateTime(statement.approved_at)},
                            {label: t("money.settlements.detail.exported"), value: formatDateTime(statement.exported_at)},
                            {label: t("money.settlements.detail.sentToBank"), value: formatDateTime(statement.sent_to_bank_at)},
                            {label: t("money.settlements.detail.paid"), value: formatDateTime(statement.paid_at)},
                            {label: t("money.settlements.detail.cancelled"), value: formatDateTime(statement.cancelled_at)},
                            statement.cancel_reason ? {label: t("money.settlements.detail.cancelReason"), value: statement.cancel_reason} : null,
                        ]} />
                    </section>
                </main>

                <ActionRail
                    actions={actions}
                    disabled={countryScope === "0" || commandMutation.isPending}
                    focusRef={actionRailRef}
                    onOpen={(action) => {
                        setCommand(action);
                        setCommandError(null);
                        setMutationResult(null);
                    }}
                />
            </div>

            <SettlementCommandDialog
                action={command}
                statement={statement}
                pending={commandMutation.isPending}
                error={commandError}
                fallbackFocusRef={actionRailRef}
                onCancel={() => {
                    setCommand(null);
                    setCommandError(null);
                }}
                onConfirm={submitCommand}
            />

            {detailQuery.isFetching ? (
                <span className="settlement-refresh-indicator" role="status">
                    <RefreshCw aria-hidden="true" size={14} /> {t("money.settlements.detail.refreshing")}
                </span>
            ) : null}
        </div>
    );
};
